import { faker } from '@faker-js/faker';

import type { StatTile } from '@/api/types';

/** State of a retry attempt — drives the second table's colored status chip. */
export type RetryStatus = 'retrying' | 'succeeded' | 'exhausted' | 'pending';

export const RETRY_STATUSES: readonly RetryStatus[] = [
	'retrying',
	'succeeded',
	'exhausted',
	'pending',
];

/** One row in the retry-attempts table. */
export interface RetryAttempt {
	id: string;
	recipient: string;
	provider: string;
	status: RetryStatus;
	attempt: number;
	maxAttempts: number;
	errorReason: string;
	/** ISO-8601 timestamp of the most recent attempt. */
	lastAttempt: string;
	/** ISO-8601 timestamp of the next scheduled retry, or null when terminal. */
	nextRetry: string | null;
}

/** Retry-processor page payload: colored stat tiles + a searchable attempts table. */
export interface RetryProcessorData {
	statTiles: StatTile[];
	attempts: RetryAttempt[];
}

const PROVIDERS = ['SES', 'SendGrid', 'Postmark', 'Mailgun'] as const;

const ERROR_REASONS = [
	'SMTP 421 — service unavailable',
	'Connection timeout',
	'Rate limit exceeded',
	'Temporary DNS failure',
	'Recipient mailbox full',
	'Greylisted by remote MTA',
] as const;

const DEFAULT_COUNT = 60;

function pct(value: number): number {
	return Number(value.toFixed(1));
}

export function getRetryProcessorMock(count = DEFAULT_COUNT): RetryProcessorData {
	const attempts: RetryAttempt[] = Array.from({ length: count }, () => {
		const status = faker.helpers.weightedArrayElement<RetryStatus>([
			{ weight: 35, value: 'retrying' },
			{ weight: 30, value: 'succeeded' },
			{ weight: 20, value: 'exhausted' },
			{ weight: 15, value: 'pending' },
		]);
		const maxAttempts = 5;
		const attempt = faker.number.int({ min: 1, max: maxAttempts });
		const terminal = status === 'succeeded' || status === 'exhausted';

		return {
			id: faker.string.uuid(),
			recipient: faker.internet.email().toLowerCase(),
			provider: faker.helpers.arrayElement(PROVIDERS),
			status,
			attempt,
			maxAttempts,
			errorReason: faker.helpers.arrayElement(ERROR_REASONS),
			lastAttempt: faker.date.recent({ days: 2 }).toISOString(),
			nextRetry: terminal ? null : faker.date.soon({ days: 1 }).toISOString(),
		};
	}).sort((a, b) => b.lastAttempt.localeCompare(a.lastAttempt));

	const inQueue = attempts.filter((a) => a.status === 'retrying' || a.status === 'pending').length;
	const succeeded = attempts.filter((a) => a.status === 'succeeded').length;
	const exhausted = attempts.filter((a) => a.status === 'exhausted').length;
	const resolved = succeeded + exhausted;

	const statTiles: StatTile[] = [
		{
			id: 'in-queue',
			label: 'In Retry Queue',
			value: inQueue.toLocaleString('en-US'),
			deltaPct: pct(faker.number.float({ min: -20, max: 20 })),
			trend: 'flat',
		},
		{
			id: 'recovered',
			label: 'Recovered',
			value: succeeded.toLocaleString('en-US'),
			deltaPct: pct(faker.number.float({ min: 0, max: 25 })),
			trend: 'up',
		},
		{
			id: 'exhausted',
			label: 'Exhausted',
			value: exhausted.toLocaleString('en-US'),
			deltaPct: pct(faker.number.float({ min: -10, max: 15 })),
			trend: 'down',
		},
		{
			id: 'recovery-rate',
			label: 'Recovery Rate',
			value: resolved === 0 ? '0%' : `${pct((succeeded / resolved) * 100)}%`,
			deltaPct: pct(faker.number.float({ min: -5, max: 10 })),
			trend: 'up',
		},
	];

	return { statTiles, attempts };
}
